import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { Router, RouterModule } from '@angular/router';
import { SnackbarService } from '../../shared/services/snackbar/snackbar.service';
import { UserForgotPasswordService } from '../../core/services/user-forgot-password/user-forgot-password.service';
import { HttpStatusCode } from 'axios';
import { MatButtonModule } from '@angular/material/button';
import { NumbersOnlyInputDirective } from '../../shared/directives/numbers-only-input/numbers-only-input.directive';

@Component({
  selector: 'app-forgot-password',
  imports: [
    CommonModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatIconModule,
    MatButtonModule,

    NumbersOnlyInputDirective
  ],
  templateUrl: './forgot-password.page.html',
  styleUrl: './forgot-password.page.scss'
})
export class ForgotPasswordPage implements OnInit {

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private snackbarService = inject(SnackbarService);
  private userForgotPasswordService = inject(UserForgotPasswordService);

  @ViewChild('forgotPasswordStepper')
  forgotPasswordStepper!: MatStepper;

  protected newPasswordHide = signal(true);
  protected confirmPasswordHide = signal(true);
  protected canShowBanButton = signal(false);
  protected isSendOtpLoading = signal(false);
  protected isSetNewPasswordLoading = signal(false);

  requestOtpFg!: FormGroup;

  setNewPasswordFg!: FormGroup;

  // protected doesPasswordHasSplChar = signal(false);
  // protected doesPasswordHasNumber = signal(false);
  // protected doesPasswordHasUppercase = signal(false);
  // protected doesPasswordHasLowercase = signal(false);
  // protected doesPasswordHasMinLength = signal(false);
  // protected doesConfirmPasswordMatches = signal(false);
  protected isNewPasswordEmpty = signal(false);

  protected doesPasswordHasUppercase: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesPasswordHasLowercase: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesPasswordHasNumber: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesPasswordHasSplChar: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesPasswordHasMinLength: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesConfirmPasswordMatches: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };
  protected doesNewPasswordEmpty: PasswordErrorCheck = {
    hasError: false,
    iconClass: "bi bi-circle",
    textClass: "hint-text text-secondary"
  };

  constructor() { }

  ngOnInit(): void {
    this.requestOtpFg = this.formBuilder.group({
      email: ["", [Validators.required, Validators.email, Validators.pattern(/^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/)]]
    });

    this.setNewPasswordFg = this.formBuilder.group({
      otp: ["", Validators.required],
      newPassword: ["", Validators.required],
      confirmPassword: ["", Validators.required]
    });
  }

  onForgotPasswordStepperIndexChange() {
    if (this.forgotPasswordStepper.selectedIndex != 0) {
      this.canShowBanButton.set(true);
    } else {
      this.canShowBanButton.set(false);
    }
  }

  newPasswordHideToggle(event: MouseEvent) {
    this.newPasswordHide.set(!this.newPasswordHide());
    event.stopPropagation();
  }

  confirmPasswordHideToggle(event: MouseEvent) {
    this.confirmPasswordHide.set(!this.confirmPasswordHide());
    event.stopPropagation();
  }

  protected async __checkPasswordConstraints() {
    const password: string = this.setNewPasswordFg.get('newPassword')?.value.toString();

    if ((new RegExp(/^(?=.*[A-Z]).*$/).test(password))) {
      this.doesPasswordHasUppercase = {
        hasError: false,
        iconClass: "bi bi-check-circle-fill",
        textClass: "hint-text text-success"
      };
    } else {
      this.doesPasswordHasUppercase = {
        hasError: false,
        iconClass: "bi bi-circle",
        textClass: "hint-text text-secondary"
      };
    }

    if (new RegExp(/^(?=.*[a-z]).*$/).test(password)) {
      this.doesPasswordHasLowercase = {
        hasError: false,
        iconClass: "bi bi-check-circle-fill",
        textClass: "hint-text text-success"
      };
    } else {
      this.doesPasswordHasLowercase = {
        hasError: false,
        iconClass: "bi bi-circle",
        textClass: "hint-text text-secondary"
      };
    }

    if (new RegExp(/^(?=.*[0-9]).*$/).test(password)) {
      this.doesPasswordHasNumber = {
        hasError: false,
        iconClass: "bi bi-check-circle-fill",
        textClass: "hint-text text-success"
      };
    } else {
      this.doesPasswordHasNumber = {
        hasError: false,
        iconClass: "bi bi-circle",
        textClass: "hint-text text-secondary"
      };
    }

    if (new RegExp(/^(?=.*[^a-zA-Z0-9]).*$/).test(password)) {
      this.doesPasswordHasSplChar = {
        hasError: false,
        iconClass: "bi bi-check-circle-fill",
        textClass: "hint-text text-success"
      };
    } else {
      this.doesPasswordHasSplChar = {
        hasError: false,
        iconClass: "bi bi-circle",
        textClass: "hint-text text-secondary"
      };
    }

    if (password.length >= 8) {
      this.doesPasswordHasMinLength = {
        hasError: false,
        iconClass: "bi bi-check-circle-fill",
        textClass: "hint-text text-success"
      };
    } else {
      this.doesPasswordHasMinLength = {
        hasError: false,
        iconClass: "bi bi-circle",
        textClass: "hint-text text-secondary"
      };
    }

    // if (new RegExp(/^(?=.*[A-Z]).*$/).test(password)) {
    //   // this.doesPasswordHasUppercase.set(true);
    // } else {
    //   // this.doesPasswordHasUppercase.set(false);
    // }

    // if (new RegExp(/^(?=.*[a-z]).*$/).test(password)) {
    //   // this.doesPasswordHasLowercase.set(true);
    // } else {
    //   // this.doesPasswordHasLowercase.set(false);
    // }

    // if (new RegExp(/^(?=.*[0-9]).*$/).test(password)) {
    //   // this.doesPasswordHasNumber.set(true);
    // } else {
    //   // this.doesPasswordHasNumber.set(false);
    // }

    // if (new RegExp(/^(?=.*[^a-zA-Z0-9]).*$/).test(password)) {
    //   // this.doesPasswordHasSplChar.set(true);
    // } else {
    //   // this.doesPasswordHasSplChar.set(false);
    // }

    // if (password.length >= 8) {
    //   // this.doesPasswordHasMinLength.set(true);
    // } else {
    //   // this.doesPasswordHasMinLength.set(false);
    // }

    // if (
    //   !this.doesPasswordHasLowercase()
    //   || !this.doesPasswordHasMinLength()
    //   || !this.doesPasswordHasNumber()
    //   || !this.doesPasswordHasSplChar()
    //   || !this.doesPasswordHasUppercase()) {
    //   this.setNewPasswordFg.controls["newPassword"].setErrors({
    //     'constraintError': true
    //   });
    // }

    if (this.setNewPasswordFg.controls['confirmPassword'].dirty) {
      this.__confirmPasswordMatch();
      this.__checkConfirmPasswordConstraintsError();
    }
  }

  protected async __checkPasswordConstraintsError() {
    const password: string = this.setNewPasswordFg.get('newPassword')?.value.toString();

    if (!new RegExp(/^(?=.*[A-Z]).*$/).test(password)) {
      this.doesPasswordHasUppercase = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };
    }

    if (!new RegExp(/^(?=.*[a-z]).*$/).test(password)) {
      this.doesPasswordHasLowercase = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };
    }

    if (!new RegExp(/^(?=.*[0-9]).*$/).test(password)) {
      this.doesPasswordHasNumber = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };
    }

    if (!new RegExp(/^(?=.*[^a-zA-Z0-9]).*$/).test(password)) {
      this.doesPasswordHasSplChar = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };
    }

    if (password.length < 8) {
      this.doesPasswordHasMinLength = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };
    }

    if (
      this.doesPasswordHasLowercase.hasError
      || this.doesPasswordHasMinLength.hasError
      || this.doesPasswordHasNumber.hasError
      || this.doesPasswordHasSplChar.hasError
      || this.doesPasswordHasUppercase.hasError) {
      this.setNewPasswordFg.controls["newPassword"].setErrors({
        'constraintError': true
      });
    } else {
      this.setNewPasswordFg.controls["newPassword"].setErrors(null);
    }
  }

  protected async __confirmPasswordMatch() {
    const password: string = this.setNewPasswordFg.get('newPassword')?.value.toString();
    const cpassword: string = this.setNewPasswordFg.get('confirmPassword')?.value.toString();

    if (this.setNewPasswordFg.controls['newPassword'].dirty) {
      if (
        password && cpassword
        && password === cpassword) {
        // this.doesConfirmPasswordMatches.set(true);

        this.doesConfirmPasswordMatches = {
          hasError: false,
          iconClass: "bi bi-check-circle-fill",
          textClass: "hint-text text-success"
        };
      } else {
        // this.doesConfirmPasswordMatches.set(false);

        this.doesConfirmPasswordMatches = {
          hasError: false,
          iconClass: "bi bi-circle",
          textClass: "hint-text text-secondary"
        };
      }

      // this.isNewPasswordEmpty.set(false);
    } else {
      this.__checkPasswordConstraintsError();
      this.__checkConfirmPasswordConstraintsError();
      this.setNewPasswordFg.controls['newPassword'].setErrors({
        'required': true
      });

      this.setNewPasswordFg.controls['newPassword'].markAsTouched();

      // this.isNewPasswordEmpty.set(true);
    }
  }

  protected __checkConfirmPasswordConstraintsError() {
    const password: string = this.setNewPasswordFg.get('newPassword')?.value.toString();
    const cpassword: string = this.setNewPasswordFg.get('confirmPassword')?.value.toString();

    if (this.setNewPasswordFg.controls['newPassword'].dirty) {
      if (
        password && cpassword
        && password !== cpassword) {
        this.doesConfirmPasswordMatches = {
          hasError: true,
          iconClass: "bi bi-x-circle-fill",
          textClass: "hint-text text-danger"
        };

        this.setNewPasswordFg.controls["confirmPassword"].setErrors({
          'constraintError': true
        });
      }
    }
    else if (!cpassword) {
      this.doesConfirmPasswordMatches = {
        hasError: true,
        iconClass: "bi bi-x-circle-fill",
        textClass: "hint-text text-danger"
      };

      this.setNewPasswordFg.controls["confirmPassword"].setErrors({
        'required': true
      });
    }

    if (!this.doesConfirmPasswordMatches.hasError) {
      this.setNewPasswordFg.controls["confirmPassword"].setErrors(null);
    }

  }

  protected async __requestOtpEmail() {
    const email: string = this.requestOtpFg.get('email')?.value.toString();

    this.isSendOtpLoading.set(true);

    const responseData = await this.userForgotPasswordService
      .forgotPasswordOtpVerification(email)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        this.requestOtpFg.controls['email'].setErrors({
          'forgotPasswordApiError': true
        });
        return error;
      });

    this.isSendOtpLoading.set(false);

    if (responseData.status == HttpStatusCode.Ok) {
      this.snackbarService.openSuccessSnackBar(responseData.data.message);
      this.forgotPasswordStepper.next();
      this.setNewPasswordFg.reset();
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      } else {
        let errorMessage: string = responseData.message.data.errormessage.toString().match(/Exception:\s*(.*)$/);
        let message = errorMessage[1] ?? "We couldn’t process your request. Please try again";
        this.snackbarService.openFailureSnackBar(message);
      }
    }
  }

  protected async __setNewPassword() {
    const email: string = this.requestOtpFg.get('email')?.value.toString();
    const otp: string = this.setNewPasswordFg.get('otp')?.value.toString();
    const password: string = this.setNewPasswordFg.get('newPassword')?.value.toString();


    this.isSetNewPasswordLoading.set(true);

    const responseData = await this.userForgotPasswordService
      .updateForgotPasswordOtpVerification(otp, email, password)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        this.requestOtpFg.controls['email'].setErrors({
          'updateForgotPasswordApiError': true
        });
        return error;
      });

    this.isSetNewPasswordLoading.set(false);

    if (responseData.status == HttpStatusCode.Ok) {
      this.snackbarService.openSuccessSnackBar(responseData.data.message);
      this.forgotPasswordStepper.reset();

      this.router.navigate(['/login']);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackbarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      } else {
        let errorMessage: string = responseData.message.data.errormessage.toString().match(/Exception:\s*(.*)$/);
        let message = errorMessage[1] ?? "We couldn’t process your request. Please try again";
        this.snackbarService.openFailureSnackBar(message);
      }
    }
  }

  protected __backBtnAction() {
    if (this.forgotPasswordStepper.selectedIndex != 0) {
      this.forgotPasswordStepper.previous();
    }
  }

}

export interface PasswordErrorCheck {
  hasError: boolean;
  iconClass: string;
  textClass: string;
}